import React, { useEffect, useRef, useState } from 'react';
import { billingApi } from '../services/api';

const SubscriptionActivation = ({ automatic, onRefresh }) => {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const running = useRef(false);
  const mounted = useRef(true);
  const started = useRef(false);

  const sync = async () => {
    if (running.current) return;
    running.current = true;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const result = await billingApi.sync();
      if (!mounted.current) return;
      if (result.status === 'active') {
        if (!onRefresh) throw new Error('Sign in again to load your activated plan.');
        const current = await onRefresh();
        if (!mounted.current) return;
        if (current.plan !== result.plan) throw new Error('Your payment is verified, but account refresh is still pending. Please retry.');
        setMessage('Your paid plan is active.');
      } else if (result.status === 'pending') {
        setMessage('Payment has not yet been confirmed for an active subscription. Use Refresh subscription to check again; do not submit another payment.');
      } else if (result.status === 'unlinked') {
        setMessage('No linked Stripe customer was found. If you already paid, contact support rather than paying again.');
      } else {
        throw new Error('This account cannot activate a subscription. Please contact support.');
      }
    } catch (err) {
      if (mounted.current) setError(err.message || 'Unable to verify your subscription.');
    } finally {
      running.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  useEffect(() => {
    mounted.current = true;
    if (automatic && !started.current) {
      started.current = true;
      sync();
    }
    return () => { mounted.current = false; };
    // Automatic reconciliation runs once on return, never on account refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [automatic]);

  return <div className="subscription-activation">
    <button type="button" className="ghost-button" disabled={busy} onClick={sync}>{busy ? 'Verifying subscription...' : 'Refresh subscription'}</button>
    {busy && <p role="status">Verifying payment securely with Stripe...</p>}
    {message && <p role="status">{message}</p>}
    {error && <div className="error-box" role="alert">{error} Refresh checks your existing payment and does not charge you again.</div>}
  </div>;
};

export default SubscriptionActivation;
